"use client";

import { useState, useEffect } from "react";
import {
  Zap,
  Gem,
  Bot,
  Laptop,
  Folder,
  FileText,
  Lock,
  Clipboard,
  Pencil,
  FlaskConical,
  CheckCircle,
  FileSignature,
  Banknote,
  Shield,
  Smartphone,
  Circle,
  Clock,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════════════
   Shared IDE Chrome — the outer "MonkeysCode" window frame
   ═══════════════════════════════════════════════════════════════════════════ */

function IDEChrome({
  children,
  title = "MonkeysCode",
  tabs,
  activeTab = 0,
}: {
  children: React.ReactNode;
  title?: string;
  tabs?: React.ReactNode[];
  activeTab?: number;
}) {
  return (
    <div className="rounded-2xl overflow-hidden select-none" style={{ background: "#0D1117", boxShadow: "0 40px 100px rgba(0,0,0,0.25), 0 0 60px rgba(108,92,231,0.04)", border: "1px solid rgba(148,163,184,0.08)" }}>
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "#010409" }}>
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <span className="flex-1 text-center text-[10px] text-[#8B949E] font-medium">{title}</span>
        <div className="w-12" />
      </div>

      {/* Tabs row */}
      {tabs && (
        <div className="flex" style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "#010409" }}>
          {tabs.map((t, i) => (
            <div key={typeof t === "string" ? t : `tab-${i}`} className="relative flex items-center gap-1 px-4 py-2 text-[10px] font-semibold transition-colors" style={{
              color: i === activeTab ? "#14B8A6" : "#484F58",
              background: i === activeTab ? "rgba(20,184,166,0.04)" : "transparent",
              borderRight: "1px solid rgba(148,163,184,0.08)",
            }}>
              {t}
              {i === activeTab && <div className="absolute bottom-0 left-1 right-1 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #10B981, #6C5CE7)" }} />}
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative overflow-hidden">
        {children}
      </div>
    </div>
  );
}

/* ── Tab icon helpers ──────────────────────────────────── */

function TabLabel({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <>
      <Icon className="w-3 h-3" />
      {label}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   1. Provider-Neutral — Chat panel with model switcher
   ═══════════════════════════════════════════════════════════════════════════ */

const MODELS = ["Capuchin", "Claude", "Gemini", "ChatGPT", "Ollama (local)"];
const MODEL_ICON_COMPONENTS: Record<string, React.ElementType> = {
  Capuchin: Zap,
  Claude: Circle,
  Gemini: Gem,
  ChatGPT: Bot,
  "Ollama (local)": Laptop,
};
const MODEL_COLORS: Record<string, string> = { Capuchin: "#10B981", Claude: "#F59E0B", Gemini: "#6C5CE7", ChatGPT: "#34D399", "Ollama (local)": "#A855F7" };

function TabIcon({ model }: { model: string }) {
  const IconComp = MODEL_ICON_COMPONENTS[model] || Zap;
  return <IconComp className="w-3 h-3" style={{ color: MODEL_COLORS[model] }} />;
}

export function MockModelSwitcher() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActive((a) => (a + 1) % MODELS.length), 2500);
    return () => clearInterval(timer);
  }, []);

  const model = MODELS[active];
  const color = MODEL_COLORS[model];

  return (
    <IDEChrome
      title="MonkeysCode"
      tabs={["main.ts", "router.php", <TabLabel key="chat" icon={Bot} label="Chat" />]}
    >
      <div className="flex min-h-[300px]">
        {/* Editor panel */}
        <div className="flex-1 p-4 font-mono text-[11px] leading-[1.9] relative" style={{ borderRight: "1px solid rgba(148,163,184,0.08)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "repeating-linear-gradient(0deg, transparent, transparent 23px, rgba(108,92,231,0.015) 23px, rgba(108,92,231,0.015) 24px)" }} />
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">1</span><span className="text-[#A855F7]">import</span> <span className="text-[#E2E8F0]">{"{ Router }"}</span> <span className="text-[#A855F7]">from</span> <span className="text-[#14B8A6]">&apos;express&apos;</span></p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">2</span></p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">3</span><span className="text-[#A855F7]">const</span> <span className="text-[#60A5FA]">router</span> <span className="text-[#8B949E]">=</span> <span className="text-[#60A5FA]">Router</span><span className="text-[#8B949E]">()</span></p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">4</span></p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">5</span><span className="text-[#60A5FA]">router</span>.<span className="text-[#FBBF24]">get</span>(<span className="text-[#14B8A6]">&apos;/api/users&apos;</span>, <span className="text-[#A855F7]">async</span> (<span className="text-[#F97316]">req</span>, <span className="text-[#F97316]">res</span>) <span className="text-[#A855F7]">=&gt;</span> {"{"}</p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">6</span>  <span className="text-[#A855F7]">const</span> <span className="text-[#60A5FA]">users</span> <span className="text-[#8B949E]">=</span> <span className="text-[#A855F7]">await</span> <span className="text-[#FBBF24]">db</span>.<span className="text-[#60A5FA]">query</span>(<span className="text-[#14B8A6]">&apos;SELECT…&apos;</span>)</p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">7</span>  <span className="text-[#F97316]">res</span>.<span className="text-[#60A5FA]">json</span>(<span className="text-[#60A5FA]">users</span>)</p>
          <p className="relative"><span className="text-[#484F58] opacity-40 w-6 inline-block select-none">8</span><span className="text-[#8B949E]">{"}"})</span></p>
        </div>

        {/* Chat panel */}
        <div className="w-[200px] flex flex-col" style={{ background: "#010409" }}>
          {/* Model switcher */}
          <div className="px-3 py-2.5 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(148,163,184,0.08)" }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
            <span className="text-[10px] font-bold flex items-center gap-1" style={{ color, transition: "color 0.3s" }} key={model}>
              <TabIcon model={model} /> {model}
            </span>
          </div>

          {/* Chat messages */}
          <div className="flex-1 p-3 space-y-3 text-[10px] overflow-hidden">
            <div className="p-2 rounded-lg" style={{ background: "rgba(108,92,231,0.06)", border: "1px solid rgba(108,92,231,0.1)" }}>
              <p className="text-[#8B949E]">Add pagination to the users endpoint</p>
            </div>
            <div className="p-2 rounded-lg" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.1)" }}>
              <p className="font-semibold mb-1 flex items-center gap-1" style={{ color }}>
                <TabIcon model={model} /> {model}
              </p>
              <p className="text-[#8B949E]">I&apos;ll add limit/offset params and update the query…</p>
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <div className="flex gap-0.5">
                <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: color, animationDelay: "0s" }} />
                <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: color, animationDelay: "0.15s" }} />
                <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: color, animationDelay: "0.3s" }} />
              </div>
              <span className="text-[9px] text-[#484F58]">editing router.ts…</span>
            </div>
          </div>

          {/* Model selector wheel */}
          <div className="px-2 py-2" style={{ borderTop: "1px solid rgba(148,163,184,0.08)" }}>
            <div className="flex gap-1 justify-center">
              {MODELS.map((m, i) => {
                const MIcon = MODEL_ICON_COMPONENTS[m] || Zap;
                return (
                  <div key={m} className="w-5 h-5 rounded-full flex items-center justify-center transition-all cursor-default" style={{
                    background: i === active ? `${MODEL_COLORS[m]}20` : "transparent",
                    border: `1px solid ${i === active ? MODEL_COLORS[m] : "rgba(148,163,184,0.08)"}`,
                    transform: i === active ? "scale(1.2)" : "scale(1)",
                    boxShadow: i === active ? `0 0 10px ${MODEL_COLORS[m]}30` : "none",
                  }}>
                    <MIcon className="w-3 h-3" style={{ color: MODEL_COLORS[m] }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </IDEChrome>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2. Local-First — File tree with indexing animation
   ═══════════════════════════════════════════════════════════════════════════ */

const FILES = [
  { name: "src/", type: "dir", depth: 0 },
  { name: "controllers/", type: "dir", depth: 1 },
  { name: "UserController.php", type: "file", depth: 2, lang: "PHP" },
  { name: "OrderController.php", type: "file", depth: 2, lang: "PHP" },
  { name: "models/", type: "dir", depth: 1 },
  { name: "User.php", type: "file", depth: 2, lang: "PHP" },
  { name: "Order.php", type: "file", depth: 2, lang: "PHP" },
  { name: "services/", type: "dir", depth: 1 },
  { name: "PaymentService.ts", type: "file", depth: 2, lang: "TS" },
  { name: "tests/", type: "dir", depth: 0 },
  { name: "user.test.ts", type: "file", depth: 1, lang: "TS" },
];

export function MockLocalIndex() {
  const [indexProgress, setIndexProgress] = useState(0);
  const [indexed, setIndexed] = useState<number[]>([]);

  useEffect(() => {
    const fileIndices = FILES.map((_, i) => i).filter((i) => FILES[i].type === "file");
    let step = 0;

    const timer = setInterval(() => {
      if (step < fileIndices.length) {
        setIndexed((prev) => [...prev, fileIndices[step]]);
        setIndexProgress(Math.round(((step + 1) / fileIndices.length) * 100));
        step++;
      } else {
        setTimeout(() => {
          setIndexed([]);
          setIndexProgress(0);
          step = 0;
        }, 3000);
      }
    }, 700);

    return () => clearInterval(timer);
  }, []);

  return (
    <IDEChrome
      title="MonkeysCode — Local Index"
      tabs={["Explorer", <TabLabel key="index" icon={Lock} label="Index" />, "Search"]}
    >
      <div className="flex min-h-[300px]">
        {/* File tree */}
        <div className="w-[180px] p-3 text-[10px] space-y-0.5" style={{ borderRight: "1px solid rgba(148,163,184,0.08)", background: "#010409" }}>
          {FILES.map((f, i) => {
            const isIndexed = indexed.includes(i);
            return (
              <div key={i} className="flex items-center gap-1.5 py-0.5 px-1 rounded transition-all" style={{
                paddingLeft: `${f.depth * 12 + 4}px`,
                background: isIndexed ? "rgba(16,185,129,0.06)" : "transparent",
              }}>
                {f.type === "dir" ? <Folder className="w-3 h-3 text-[#8B949E]" /> : <FileText className="w-3 h-3 text-[#484F58]" />}
                <span className={`${f.type === "dir" ? "font-semibold text-[#8B949E]" : "text-[#484F58]"} transition-colors`} style={{
                  color: isIndexed ? "#10B981" : undefined,
                }}>
                  {f.name}
                </span>
                {isIndexed && <CheckCircle className="ml-auto w-3 h-3 text-[#10B981]" />}
              </div>
            );
          })}
        </div>

        {/* Index status panel */}
        <div className="flex-1 p-5">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#14B8A6] flex items-center gap-1.5">
                {indexProgress < 100 ? <><Zap className="w-3 h-3" /> Indexing…</> : <><CheckCircle className="w-3 h-3" /> Indexed</>}
              </span>
              <span className="text-[10px] font-mono text-[#484F58]">{indexProgress}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.08)" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${indexProgress}%`,
                background: indexProgress < 100 ? "linear-gradient(90deg, #10B981, #14B8A6)" : "#10B981",
                boxShadow: `0 0 12px rgba(16,185,129,0.3)`,
              }} />
            </div>
          </div>

          <div className="space-y-3 text-[10px] font-mono">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#10B981]" />
              <span className="text-[#8B949E]">Tree-sitter parse: chunks</span>
              <span className="ml-auto text-[#14B8A6]">247</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#14B8A6]" />
              <span className="text-[#8B949E]">Embeddings: vectors</span>
              <span className="ml-auto text-[#14B8A6]">247</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#6C5CE7]" />
              <span className="text-[#8B949E]">Symbol graph: edges</span>
              <span className="ml-auto text-[#14B8A6]">89</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#10B981]" />
              <span className="text-[#8B949E]">Merkle root</span>
              <span className="ml-auto text-[#10B981] font-semibold">a7f2c…</span>
            </div>
          </div>

          <div className="mt-6 p-3 rounded-lg text-[10px] flex items-center gap-1.5" style={{ background: "rgba(16,185,129,0.04)", border: "1px solid rgba(16,185,129,0.1)" }}>
            <Lock className="w-3 h-3 text-[#10B981]" />
            <span className="text-[#10B981] font-bold">Air-gapped</span>
            <span className="text-[#484F58]"> — zero outbound connections</span>
          </div>
        </div>
      </div>
    </IDEChrome>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3. Verifiable — Signed run viewer
   ═══════════════════════════════════════════════════════════════════════════ */

const STEP_ICONS: Record<string, React.ElementType> = {
  clipboard: Clipboard,
  pencil: Pencil,
  flask: FlaskConical,
  check: CheckCircle,
  signature: FileSignature,
};

export function MockSignedRun() {
  const [step, setStep] = useState(0);
  const steps = [
    { status: "done", label: "Plan: Add CSRF $except support", icon: "clipboard" },
    { status: "done", label: "Edit: VerifyCsrfToken.php", icon: "pencil" },
    { status: "done", label: "Edit: middleware.mlc", icon: "pencil" },
    { status: "done", label: "Run: php artisan test", icon: "flask" },
    { status: "verify", label: "14/14 tests passed", icon: "check" },
    { status: "sign", label: "Signed: run #a7f2c", icon: "signature" },
  ];

  useEffect(() => {
    const timer = setInterval(() => setStep((s) => (s + 1) % (steps.length + 3)), 1000);
    return () => clearInterval(timer);
  }, [steps.length]);

  return (
    <IDEChrome
      title="MonkeysCode — Signed Run"
      tabs={["Editor", <TabLabel key="runlog" icon={FileSignature} label="Run Log" />, "Diff"]}
    >
      <div className="flex min-h-[300px]">
        {/* Run log */}
        <div className="flex-1 p-4 space-y-2.5">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#14B8A6]">Agent Run #a7f2c</span>
            <span className="px-2 py-0.5 rounded text-[8px] font-bold uppercase" style={{
              background: step >= steps.length ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)",
              color: step >= steps.length ? "#10B981" : "#F59E0B",
            }}>
              {step >= steps.length ? "Signed" : "Running…"}
            </span>
          </div>

          {steps.map((s, i) => {
            const isActive = i === step;
            const isDone = i < step;
            const StepIcon = STEP_ICONS[s.icon] || Clipboard;
            return (
              <div key={i} className="flex items-center gap-3 py-1.5 px-2 rounded transition-all" style={{
                background: isActive ? "rgba(108,92,231,0.06)" : "transparent",
                opacity: isDone || isActive ? 1 : 0.3,
              }}>
                <span className="w-5 h-5 rounded flex items-center justify-center" style={{
                  background: isDone ? "rgba(16,185,129,0.1)" : isActive ? "rgba(108,92,231,0.1)" : "rgba(148,163,184,0.08)",
                  border: `1px solid ${isDone ? "rgba(16,185,129,0.2)" : isActive ? "rgba(108,92,231,0.2)" : "rgba(148,163,184,0.08)"}`,
                }}>
                  {isDone ? <CheckCircle className="w-3 h-3 text-[#10B981]" /> : isActive ? <span className="text-[#14B8A6] text-[8px]">▶</span> : <span className="text-[#484F58] text-[8px]">·</span>}
                </span>
                <span className="text-[11px] font-medium flex items-center gap-1" style={{
                  color: isDone ? "#10B981" : isActive ? "#14B8A6" : "#484F58",
                }}>
                  <StepIcon className="w-3 h-3" /> {s.label}
                </span>
                {isActive && (
                  <div className="ml-auto flex gap-0.5">
                    <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: "#14B8A6", animationDelay: "0s" }} />
                    <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: "#14B8A6", animationDelay: "0.15s" }} />
                    <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: "#14B8A6", animationDelay: "0.3s" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Diff panel */}
        <div className="w-[180px] p-3 font-mono text-[9px] leading-[1.8]" style={{ borderLeft: "1px solid rgba(148,163,184,0.08)", background: "#010409" }}>
          <p className="text-[8px] text-[#484F58] uppercase tracking-wider font-sans font-bold mb-2">Diff Preview</p>
          <p><span className="text-[#EF4444]">- $except = [];</span></p>
          <p><span className="text-[#10B981]">+ $except = [</span></p>
          <p><span className="text-[#10B981]">+   &apos;/api/webhook&apos;,</span></p>
          <p><span className="text-[#10B981]">+   &apos;/api/stripe&apos;,</span></p>
          <p><span className="text-[#10B981]">+ ];</span></p>
          <p className="mt-3 text-[#484F58]">{"// shouldSkip()"}</p>
          <p><span className="text-[#10B981]">+ if ($this→inEx…</span></p>
          <div className="mt-4 pt-3" style={{ borderTop: "1px solid rgba(148,163,184,0.08)" }}>
            <p className="text-[8px] text-[#484F58] font-sans mb-1">SIGNATURE</p>
            <p className="text-[#14B8A6] break-all text-[8px]">sha256:e4a9f2c7…d31b</p>
          </div>
        </div>
      </div>
    </IDEChrome>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   4. Cost — Budget dashboard
   ═══════════════════════════════════════════════════════════════════════════ */

export function MockCostDashboard() {
  const [budget, setBudget] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setBudget((b) => b >= 100 ? 0 : b + 2), 200);
    return () => clearInterval(timer);
  }, []);

  const usage = Math.min(budget, 72);
  const barColor = usage > 60 ? "#F59E0B" : "#10B981";

  return (
    <IDEChrome
      title="MonkeysCode — Cost Dashboard"
      tabs={["Editor", <TabLabel key="budget" icon={Banknote} label="Budget" />, "Settings"]}
    >
      <div className="p-5 min-h-[300px] space-y-5">
        {/* Header stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "TODAY", value: "$0.42", color: "#10B981" },
            { label: "THIS WEEK", value: "$2.87", color: "#14B8A6" },
            { label: "BUDGET LEFT", value: "$47.13", color: "#6C5CE7" },
          ].map((s) => (
            <div key={s.label} className="p-3 rounded-lg text-center" style={{ background: "rgba(108,92,231,0.04)", border: "1px solid rgba(148,163,184,0.08)" }}>
              <p className="text-[8px] font-bold uppercase tracking-wider text-[#484F58] mb-1">{s.label}</p>
              <p className="text-[16px] font-black" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Per-model breakdown */}
        <div className="space-y-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#484F58]">Per-Model Usage</p>
          {[
            { icon: Zap, name: "Capuchin", pct: 45, cost: "$0 (included)", color: "#10B981" },
            { icon: Smartphone, name: "ChatGPT", pct: 28, cost: "$1.24 BYOK", color: "#34D399" },
            { icon: Laptop, name: "Ollama", pct: 22, cost: "$0 (local)", color: "#A855F7" },
            { icon: Gem, name: "Gemini", pct: 5, cost: "$0.18 BYOK", color: "#6C5CE7" },
          ].map((m) => (
            <div key={m.name} className="flex items-center gap-3">
              <span className="text-[10px] w-[100px] shrink-0 text-[#8B949E] flex items-center gap-1">
                <m.icon className="w-3 h-3" style={{ color: m.color }} /> {m.name}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.08)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${m.pct}%`, background: m.color }} />
              </div>
              <span className="text-[9px] text-[#484F58] w-[70px] text-right shrink-0">{m.cost}</span>
            </div>
          ))}
        </div>

        {/* Budget bar */}
        <div className="p-3 rounded-lg" style={{ background: "rgba(16,185,129,0.04)", border: "1px solid rgba(148,163,184,0.08)" }}>
          <div className="flex justify-between mb-2">
            <span className="text-[10px] font-bold text-[#8B949E] flex items-center gap-1">
              <Shield className="w-3 h-3" /> Hard Cap: $50/mo
            </span>
            <span className="text-[10px] font-mono" style={{ color: barColor }}>{usage}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.08)" }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${usage}%`, background: barColor, boxShadow: `0 0 8px ${barColor}` }} />
          </div>
          <p className="text-[9px] text-[#484F58] mt-1.5">Agent stops when budget is reached — no surprise bills</p>
        </div>
      </div>
    </IDEChrome>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5. Agents — Parallel agent panels
   ═══════════════════════════════════════════════════════════════════════════ */

export function MockParallelAgents() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => (t + 1) % 20), 800);
    return () => clearInterval(timer);
  }, []);

  const agents = [
    {
      name: "Agent A",
      color: "#10B981",
      file: "UserController.php",
      steps: ["Planning…", "Editing UserController.php", "Editing User.php", "Running tests…", "4/4 passed"],
    },
    {
      name: "Agent B",
      color: "#6C5CE7",
      file: "OrderService.ts",
      steps: ["Planning…", "Editing OrderService.ts", "Editing order.test.ts", "Running tests…", "6/6 passed"],
    },
    {
      name: "Agent C",
      color: "#14B8A6",
      file: "migration_001.sql",
      steps: ["Planning…", "Creating migration", "Editing schema.prisma", "Validating…", "Schema valid"],
    },
  ];

  return (
    <IDEChrome
      title="MonkeysCode — Parallel Agents"
      tabs={["Editor", <TabLabel key="agents" icon={Bot} label="Agents" />, "Merge"]}
    >
      <div className="p-4 min-h-[300px] space-y-3">
        {/* Agent status header */}
        <div className="flex items-center gap-4 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#14B8A6]">3 Agents Running</span>
          <div className="flex-1 h-px" style={{ background: "rgba(148,163,184,0.08)" }} />
          <span className="text-[10px] font-mono text-[#484F58]">{Math.min(tick * 5, 100)}%</span>
        </div>

        {/* Agent lanes */}
        {agents.map((a, ai) => {
          const agentTick = Math.max(0, tick - ai * 2);
          const stepIdx = Math.min(agentTick, a.steps.length - 1);
          const isDone = agentTick >= a.steps.length - 1;
          return (
            <div key={a.name} className="p-3 rounded-xl transition-all" style={{
              background: "rgba(108,92,231,0.03)",
              border: `1px solid ${isDone ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.08)"}`,
            }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full" style={{ background: a.color, boxShadow: isDone ? "none" : `0 0 8px ${a.color}` }} />
                <span className="text-[10px] font-bold" style={{ color: a.color }}>{a.name}</span>
                <span className="text-[9px] text-[#484F58]">→ {a.file}</span>
                <span className="ml-auto px-2 py-0.5 rounded text-[8px] font-bold uppercase flex items-center gap-1" style={{
                  background: isDone ? "rgba(16,185,129,0.1)" : `rgba(108,92,231,0.06)`,
                  color: isDone ? "#10B981" : "#484F58",
                }}>
                  {isDone ? <><CheckCircle className="w-2.5 h-2.5" /> Done</> : "Active"}
                </span>
              </div>
              {/* Progress steps */}
              <div className="flex gap-1">
                {a.steps.map((s, si) => (
                  <div key={si} className="h-1 flex-1 rounded-full transition-all duration-500" style={{
                    background: si <= stepIdx ? a.color : "rgba(148,163,184,0.08)",
                    opacity: si <= stepIdx ? 1 : 0.3,
                  }} />
                ))}
              </div>
              <p className="text-[9px] mt-1.5 text-[#484F58]">{a.steps[stepIdx]}</p>
            </div>
          );
        })}

        {/* Merge status */}
        <div className="flex items-center gap-2 p-2 rounded-lg" style={{
          background: tick >= 16 ? "rgba(16,185,129,0.06)" : "rgba(108,92,231,0.03)",
          border: `1px solid ${tick >= 16 ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.08)"}`,
        }}>
          {tick >= 16 ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> : <Clock className="w-3.5 h-3.5 text-[#484F58]" />}
          <span className="text-[10px] font-semibold" style={{ color: tick >= 16 ? "#10B981" : "#484F58" }}>
            {tick >= 16 ? "All agents merged — 0 conflicts" : "Waiting for all agents to complete…"}
          </span>
        </div>
      </div>
    </IDEChrome>
  );
}
